import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { siteConfig, IMAGES } from "@/data/siteConfig";
import { scrollToId } from "./shared";
import { ArrowRight } from "lucide-react";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-navy grain"
    >
      <motion.div style={{ y }} className="absolute inset-0 z-0">
        <img
          src={IMAGES.hero}
          alt="Young men of the Club standing together"
          className="h-[120%] w-full object-cover object-center"
        />
      </motion.div>
      <motion.div
        style={{ opacity: overlay }}
        className="absolute inset-0 z-[1] bg-navy"
      />
      <div className="absolute inset-0 z-[1] bg-gradient-to-t from-navy via-navy/30 to-navy/60" />

      <div className="relative z-10 mx-auto flex min-h-screen max-w-[1400px] flex-col justify-end px-6 pb-20 pt-32 lg:px-12">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.8 }}
          className="mb-8 text-xs uppercase tracking-[0.35em] text-gold"
        >
          {siteConfig.hero.eyebrow}
        </motion.p>

        <h1 className="font-display text-5xl font-semibold leading-[1.02] tracking-tight text-cream sm:text-6xl lg:text-[5.2rem]">
          {siteConfig.hero.headlineLines.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span
                className="block"
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          className="mt-8 max-w-2xl text-lg leading-relaxed text-cream/80 md:text-xl"
        >
          {siteConfig.hero.subhead}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.8 }}
          className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <button
            data-testid="hero-apply-btn"
            onClick={() => scrollToId("#apply")}
            className="group inline-flex items-center justify-center gap-3 bg-gold px-8 py-4 text-sm font-semibold tracking-wide text-navy transition-colors duration-300 hover:bg-cream"
          >
            {siteConfig.primaryCta}
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-learn-btn"
            onClick={() => scrollToId("#mission")}
            className="inline-flex items-center justify-center gap-3 border border-cream/30 px-8 py-4 text-sm font-semibold tracking-wide text-cream transition-colors duration-300 hover:border-cream hover:bg-cream/5"
          >
            {siteConfig.secondaryCta}
          </button>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 0.8 }}
          className="mt-12 text-xs uppercase tracking-[0.3em] text-cream/50"
        >
          {siteConfig.hero.founderLine}
        </motion.p>
      </div>
    </section>
  );
};
